import { useEffect } from 'react'
import maplibregl from 'maplibre-gl'
import { loadStructureIcons, loadCountBadges } from '../../utils/structures'
import { useMapStore, mapState } from '../../store/mapStore'
import { apiFetch } from '../../api/client'

// One-time map bootstrap: create the MapLibre instance, load both projection
// GeoJSON datasets (mercator clamped / globe unclamped), add the regions + border
// + hover + satellite + topography layers, and wire the region click / infra
// free-draw / right-click-erase / hover handlers. Runs once (empty deps); live
// values come from the store via mapState(), stable callbacks from props.
export function useMapInit(map, mapContainer, previousCustomColorsRef, {
  isGlobeView, onRegionsDataLoaded, onRegionClick, onRulerClick, onRangeClick,
  onInfrastructureClick, onStructurePlace, onRegionPaint, onRegionErase,
  onMapLoaded, setRegionsData, setRegionsMercatorData, setRegionsGlobeData,
  setZoomBucket, mapBackground,
}) {
  // Initialize map
  useEffect(() => {
    if (map.current) return // Map already initialized

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        sources: {
          satelliteSource: {
            type: 'raster',
            tiles: [
              'https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2020_3857/default/g/{z}/{y}/{x}.jpg'
            ],
            tileSize: 256
          },
          topographySource: {
            type: 'raster',
            tiles: [
              'https://tiles.maps.eox.at/wmts/1.0.0/terrain-light_3857/default/g/{z}/{y}/{x}.jpg'
            ],
            tileSize: 256
          }
        },
        layers: [
          {
            id: 'background',
            type: 'background',
            paint: {
              'background-color': mapBackground || '#1a1a1a'
            }
          }
        ],
        projection: { type: 'mercator' }
      },
      center: [20, 20],
      zoom: 2,
      minZoom: 0.5,
      maxZoom: 10,
      renderWorldCopies: true,  // Start true for plane mode, toggled for globe
      preserveDrawingBuffer: true // required for reliable canvas.toDataURL() (PNG/PDF export)
    })

    // Recluster structure icons on zoom changes. Integer buckets on purpose:
    // the cluster grid cell is radius/2^zoom, so integer zooms make each
    // cell an exact half of the previous level's — clusters split/merge
    // hierarchically instead of reshuffling membership at every half-zoom
    // (the old 0.5 granularity made stacking look inconsistent when zooming
    // in and back out to the same level).
    map.current.on('zoomend', () => {
      setZoomBucket(Math.floor(map.current.getZoom()))
    })

    map.current.on('load', async () => {
      // Register structure icons + count badges early (non-blocking) so
      // they're already available by the time the layer is first toggled on.
      loadStructureIcons(map.current).catch(() => {})
      try { loadCountBadges(map.current) } catch { /* badges just won't render */ }

      // Load BOTH GeoJSON versions (mercator with clamp, globe without clamp)
      try {
        console.log('Loading both GeoJSON versions...')
        const [responseMercator, responseGlobe] = await Promise.all([
          apiFetch('/geojson/regions?projection=mercator', { timeout: 30000 }),
          apiFetch('/geojson/regions?projection=globe', { timeout: 30000 })
        ])
        
        const dataMercator = await responseMercator.json()
        const dataGlobe = await responseGlobe.json()
        
        console.log('✅ Loaded Mercator version:', dataMercator.features.length, 'features')
        console.log('✅ Loaded Globe version:', dataGlobe.features.length, 'features')
        
        // Process both datasets with shared metadata (area index, centroids)
        const processGeoJSON = (data) => {
          // Pre-build geographical area index for O(1) lookups during area painting
          const areaIndex = new Map()
          data.features.forEach(feature => {
            const areaName = feature.properties.geographical_area
            if (areaName) {
              if (!areaIndex.has(areaName)) {
                areaIndex.set(areaName, [])
              }
              areaIndex.get(areaName).push(feature.properties.region_id)
            }
            
            // Pre-calculate centroids for O(1) center-to-region operations
            let lng = 0, lat = 0, count = 0
            if (feature.geometry.type === 'Polygon') {
              feature.geometry.coordinates[0].forEach(coord => {
                lng += coord[0]
                lat += coord[1]
                count++
              })
            } else if (feature.geometry.type === 'MultiPolygon') {
              feature.geometry.coordinates.forEach(polygon => {
                polygon[0].forEach(coord => {
                  lng += coord[0]
                  lat += coord[1]
                  count++
                })
              })
            }
            if (count > 0) {
              feature.properties._centroid = [lng / count, lat / count]
            }
          })
          // Store area index separately (can't be serialized by MapLibre)
          return { ...data, _areaIndex: areaIndex }
        }
        
        const dataWithIndexMercator = processGeoJSON(dataMercator)
        const dataWithIndexGlobe = processGeoJSON(dataGlobe)
        
        setRegionsMercatorData(dataWithIndexMercator)
        setRegionsGlobeData(dataWithIndexGlobe)
        
        // Set active data based on current view mode
        const activeData = isGlobeView ? dataWithIndexGlobe : dataWithIndexMercator
        setRegionsData(activeData)
        console.log('📍 Active projection:', isGlobeView ? 'Globe' : 'Mercator')
        
        // Set initial projection
        map.current.setProjection({
          type: isGlobeView ? 'globe' : 'mercator'
        })
        
        // Force background color after everything is loaded
        map.current.once('idle', () => {
          if (!isGlobeView) {
            map.current.setPaintProperty('background', 'background-color', mapBackground || '#1a1a1a')
          }
        })
        
        // Notify parent that data is loaded (for country count, URL region lookup, etc.)
        if (onRegionsDataLoaded) {
          onRegionsDataLoaded(activeData)
        }

        // Add regions source with promoteId for feature-state support
        // Use clean data without _areaIndex (Map objects can't be serialized)
        map.current.addSource('regions', {
          type: 'geojson',
          data: isGlobeView ? dataGlobe : dataMercator,
          promoteId: 'region_id' // Use region_id as the feature ID for feature-state
        })

        // Add base regions layer
        // NOTE: fill-antialias must be false — with antialiased fills, each polygon
        // edge gets a semi-transparent fringe. On high-detail polygons this creates
        // dozens of "cuts" through the interior, shattering regions into disconnected
        // fragments. The separate regions-border line layer provides clean edges.
        map.current.addLayer({
          id: 'regions-fill',
          type: 'fill',
          source: 'regions',
          paint: {
            'fill-color': '#cccccc',
            'fill-opacity': 0.8,
            'fill-antialias': false
          }
        })

        // Add region borders
        map.current.addLayer({
          id: 'regions-border',
          type: 'line',
          source: 'regions',
          paint: {
            'line-color': '#333333',
            'line-width': 1
          }
        })

        // Country borders (dissolved outlines) — computed lazily on first use
        // of "Pays" border mode, see the borderMode effect below.
        map.current.addSource('countryBordersSource', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] }
        })
        map.current.addLayer({
          id: 'country-border',
          type: 'line',
          source: 'countryBordersSource',
          layout: { visibility: 'none' },
          paint: {
            'line-color': '#333333',
            'line-width': 1
          }
        })

        // Add hover effect using feature-state (avoids setFilter performance cost)
        map.current.addLayer({
          id: 'regions-hover',
          type: 'line',
          source: 'regions',
          paint: {
            'line-color': '#ffffff',
            'line-width': [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
              3,
              0
            ]
          }
        })
        
        // Add satellite layer (initially hidden)
        // Insert before regions-fill so regions are visible on top
        map.current.addLayer({
          id: 'satellite',
          type: 'raster',
          source: 'satelliteSource',
          layout: { visibility: 'none' },
          paint: { 'raster-opacity': 0.6 }
        }, 'regions-fill')

        // Topography (terrain/relief) raster — driven by the Topographie
        // radio in the sidebar. Regions become mostly transparent on top.
        map.current.addLayer({
          id: 'topography',
          type: 'raster',
          source: 'topographySource',
          layout: { visibility: 'none' },
          paint: { 'raster-opacity': 0.9 }
        }, 'regions-fill')

        // Handle region clicks. In Plan Mode, tapping an already-painted
        // region erases it instead of repainting — this is the only erase
        // gesture available on touch devices (no right-click), so it doubles
        // as the mobile-friendly path; desktop right-click below still works too.
        map.current.on('click', 'regions-fill', (e) => {
          // A drag gesture (structure move / free-draw) just ended — the
          // browser still fires a click on release; swallow it so it doesn't
          // double as a build/waypoint action.
          if (mapState().suppressNextMapClick && Date.now() - mapState().suppressNextMapClick < 400) {
            useMapStore.setState({ suppressNextMapClick: 0 })
            return
          }
          if (e.features.length > 0) {
            const region = e.features[0].properties

            if (mapState().rangeModeActive && onRangeClick) {
              onRangeClick(e.lngLat)
            } else if (mapState().rulerModeActive && onRulerClick) {
              onRulerClick(e.lngLat)
            } else if (mapState().infrastructureModeActive && mapState().infraMode === 'draw' && onInfrastructureClick) {
              onInfrastructureClick(region, e.lngLat)
            } else if (mapState().constructionModeActive && onStructurePlace) {
              onStructurePlace(region, e.lngLat)
            } else if (mapState().planModeActive && onRegionPaint) {
              const alreadyPainted = !!previousCustomColorsRef.current[region.region_id]
              if (alreadyPainted && onRegionErase) {
                onRegionErase(region.region_id, region.geographical_area)
              } else {
                onRegionPaint(region.region_id, region.geographical_area)
              }
            } else {
              // Shift / Ctrl / Cmd + click toggles the region in the
              // multi-selection set (for aggregated regional analysis) instead
              // of opening the single-region popup.
              const oe = e.originalEvent
              if (oe.shiftKey || oe.ctrlKey || oe.metaKey) {
                useMapStore.getState().toggleSelectedRegion(region.region_id)
              } else {
                onRegionClick(region)
              }
            }
          }
        })

        // Infrastructure free-draw: in Infrastructure Mode's 'draw' sub-mode the
        // left button draws instead of panning. Press adds the first node; holding and
        // moving samples nodes continuously (≥10px apart on screen) along
        // the cursor path; release ends the stroke. A plain click (press +
        // release without moving) therefore still adds a single waypoint —
        // the two gestures share this one handler, and the click event that
        // fires after release is always swallowed via __suppressNextMapClick.
        map.current.on('mousedown', 'regions-fill', (e) => {
          if (!mapState().infrastructureModeActive || !onInfrastructureClick) return
          // 'select' mode hands every mouse gesture back to the map: pan, zoom
          // and route-picking, never an accidental stroke.
          if (mapState().infraMode !== 'draw') return
          if (e.originalEvent.button !== 0) return
          // A structure drag has priority when both layers overlap
          if (mapState().structDragActive) return
          // A press on an existing path node is a node move / fork (handled
          // by the infra-path-points-hit handler), not a new stroke. The hit
          // layer's enlarged radius is the effective guard zone.
          if (
            map.current.getLayer('infra-path-points-hit') &&
            map.current.queryRenderedFeatures(e.point, { layers: ['infra-path-points-hit'] }).length > 0
          ) return
          // Nothing drawn yet + press on an existing route: that's the
          // "rework this path" gesture — let the infrastructure-lines click
          // handler take it instead of starting a stroke here.
          const infraLineLayers = ['infrastructure-lines', 'infrastructure-lines-dash', 'infrastructure-lines-dot']
            .filter((id) => map.current.getLayer(id))
          if (
            (mapState().infrastructurePath || []).length === 0 &&
            infraLineLayers.length &&
            map.current.queryRenderedFeatures(e.point, { layers: infraLineLayers }).length > 0
          ) return
          // Pressing on a region you don't own can't start a stroke — let
          // the event through untouched so the map pans normally. That's the
          // navigation escape hatch while drawing: grab any foreign/neutral
          // region to move around, draw only starts on your own territory.
          const pressRegion = e.features?.[0]?.properties
          if (!pressRegion || pressRegion.country_id !== mapState().ownedCountryId) return
          e.preventDefault() // keep the map from panning while drawing

          onInfrastructureClick(pressRegion, e.lngLat)

          // Edge auto-pan: holding the stroke near a viewport edge scrolls
          // the map in that direction, so long routes don't require
          // releasing the stroke to navigate. Runs on an interval because
          // mousemove stops firing when the cursor is parked at the edge.
          const EDGE_PX = 48
          const PAN_STEP = 14
          let lastScreen = e.point
          const edgePan = setInterval(() => {
            const canvas = map.current.getCanvas()
            const w = canvas.clientWidth
            const h = canvas.clientHeight
            let dx = 0
            let dy = 0
            if (lastScreen.x < EDGE_PX) dx = -PAN_STEP
            else if (lastScreen.x > w - EDGE_PX) dx = PAN_STEP
            if (lastScreen.y < EDGE_PX) dy = -PAN_STEP
            else if (lastScreen.y > h - EDGE_PX) dy = PAN_STEP
            if (dx || dy) map.current.panBy([dx, dy], { duration: 0 })
          }, 50)

          let lastPoint = e.point
          const sample = (ev) => {
            lastScreen = ev.point
            const dx = ev.point.x - lastPoint.x
            const dy = ev.point.y - lastPoint.y
            if (dx * dx + dy * dy < 484) return // sample every ~22px of cursor travel (App also thins collinear runs)
            lastPoint = ev.point
            const feats = map.current.queryRenderedFeatures(ev.point, { layers: ['regions-fill'] })
            // Only own territory adds nodes; crossing a foreign region just
            // skips (the App handler re-checks and reports anyway).
            if (feats.length > 0) {
              onInfrastructureClick(feats[0].properties, ev.lngLat)
            }
          }
          const finish = () => {
            clearInterval(edgePan)
            map.current.off('mousemove', sample)
            useMapStore.setState({ suppressNextMapClick: Date.now() })
          }
          map.current.on('mousemove', sample)
          map.current.once('mouseup', finish)
        })

        // Handle right-click for erasing paint in plan mode
        map.current.on('contextmenu', 'regions-fill', (e) => {
          if (e.features.length > 0) {
            const region = e.features[0].properties
            
            if (mapState().planModeActive && onRegionErase) {
              e.preventDefault() // Prevent default context menu
              onRegionErase(region.region_id, region.geographical_area)
            }
          }
        })

        // Create popup for hover
        const popup = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false,
          className: 'region-hover-popup'
        })

        // Handle hover with requestAnimationFrame throttling + feature-state (prevents 60-120 events/sec)
        let hoveredRegionId = null
        let hoverRaf = null
        
        map.current.on('mousemove', 'regions-fill', (e) => {
          if (hoverRaf) return // Already queued, skip
          
          // Capture data immediately (before RAF) as e.features may become invalid
          const features = e.features
          const lngLat = e.lngLat
          
          hoverRaf = requestAnimationFrame(() => {
            hoverRaf = null
            
            if (features && features.length > 0) {
              const feature = features[0]
              const props = feature.properties
              
              // Only update if region changed
              if (hoveredRegionId !== props.region_id) {
                // Clear previous hover state (feature-state instead of setFilter = faster)
                if (hoveredRegionId !== null) {
                  try {
                    map.current.setFeatureState(
                      { source: 'regions', id: hoveredRegionId },
                      { hover: false }
                    )
                  } catch (e) { /* ignore */ }
                }
                
                hoveredRegionId = props.region_id
                
                // Set new hover state
                try {
                  map.current.setFeatureState(
                    { source: 'regions', id: hoveredRegionId },
                    { hover: true }
                  )
                } catch (e) { /* ignore */ }
                
                // Show popup with region info (only when region changes)
                const popupContent = `
                  <div style="font-size: 12px; padding: 4px;">
                    <strong>#${props.region_id}</strong> | ${props.name}<br/>
                    <em>${props.geographical_area || 'Unknown'}</em>
                  </div>
                `
                popup.setLngLat(lngLat).setHTML(popupContent).addTo(map.current)
              }
            }
            
            // Set cursor only once per RAF cycle
            map.current.getCanvas().style.cursor = (mapState().planModeActive || mapState().constructionModeActive) ? 'crosshair' : 'pointer'
          })
        })

        map.current.on('mouseleave', 'regions-fill', () => {
          if (hoveredRegionId !== null) {
            try {
              map.current.setFeatureState(
                { source: 'regions', id: hoveredRegionId },
                { hover: false }
              )
            } catch (e) { /* ignore */ }
          }
          hoveredRegionId = null
          popup.remove()
          map.current.getCanvas().style.cursor = ''
        })

        onMapLoaded()
      } catch (error) {
        console.error('Error loading map data:', error)
      }
    })

    // Cleanup
    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
      }
    }
  }, [])
}
